import type { FormikProps } from "formik";
import type { ProgressBarStatus } from "@financeable-com-au/financeable-ui";
import type { Fee, InArrears, SubmissionStep } from "@/store/slices/types/applicationFormSlice";
import type { UseLenderCardResult } from "@/components/LenderProductCard/hooks/useLenderCard";
import type { UseLenderCardCustomResult } from "@/components/LenderProductCard/hooks/useLenderCardCustom";
import type { QuoteCalculatorFormFields } from "@/components/LenderProductCard/hooks/useQuoteCalculatorForm";

interface LenderCredential {
  label: string;
  attribute: string;
}

interface RemainingDataFields {
  asset: string[];
  entity: string[];
  customer: string[];
  agreement: string[];
  loanDetails: string[];
}

interface RawRequiredFields {
  asset?: string[];
  entity?: string[];
  customer?: string[];
  agreement?: string[];
  loanDetails?: string[];
}

interface RequiredFieldsData {
  remainingDataFields: RemainingDataFields;
  requiredFields: string[];
  remainingRequiredFields: string[];
}

enum ConditionalSection {
  AGREEMENT = "agreement",
  ASSET = "asset",
  CUSTOMER = "customer",
  ENTITY = "entity",
  LOAN_DETAILS = "loanDetails",
}

enum LogicalOperatorRules {
  IS_EQUAL = "is_equal",
  IS_NOT_EQUAL = "is_not_equal",
  IS_GREATER_THAN = "is_greater_than",
  IS_LESS_THAN = "is_less_than",
  IS_EMPTY = "is_empty",
  IS_NOT_EMPTY = "is_not_empty",
}

enum LogicalOperators {
  ANY = "any",
  ALL = "all",
}

interface Condition {
  rule: LogicalOperatorRules;
  ruleValue?: string;
  section: ConditionalSection;
  subSection: string;
  sourceField: string;
}

interface Validation {
  rule: LogicalOperatorRules;
  ruleValue?: string | number;
  aggregation?: "sum" | "none";
}

interface ConditionalField {
  section: ConditionalSection;
  subSection: string;
  targetField: string;
  conditionType: LogicalOperators;
  conditions?: Condition[];
  hasValidation?: boolean;
  validationType: LogicalOperators;
  validationMessage?: string;
  validations?: Validation[];
}

interface LoadingCriteria {
  label: string;
  object: string;
  attribute: string;
  condition: string;
  value: { min: number; max: number };
}

interface LoadingItem {
  name: string;
  value: number;
  valueType: string;
  criteria: LoadingCriteria[];
  result: boolean;
}

interface Loading {
  loadingList: LoadingItem[];
  loadingTotal: number;
}

interface CriteriaResult {
  label: string;
  object: string;
  attribute: string;
  condition: string;
  value: string | number | string[] | { min: number; max: number };
  result?: boolean;
}

interface RequiredFields {
  remainingFields: RemainingDataFields;
  fieldsTotalLength: number;
  fieldsCheckedPercentage: number;
  fieldsDoneLength: number;
  fieldsStatus: ProgressBarStatus;
  isFieldsCompleted: boolean;
}

interface Range {
  min: number;
  max: number;
}

export interface LodgeAI {
  cloud: boolean;
  native: boolean;
}

interface LenderProduct {
  lender: string;
  lenderId: string;
  productName: string;
  name: string;
  logo?: string;
  lenderCredentials: LenderCredential[];
  requiredFieldsData?: RequiredFieldsData | undefined;
  brokerage?: number;
  brokerageAmount: number;
  brokerageMax: number;
  fees: Fee[];
  allFees: Fee[];
  loading: Loading;
  criteriaResults: CriteriaResult[];
  fit: number;
  criteria: string;
  qualification: number;
  productType: string;
  labels: string[];
  rate: number;
  rateRange: Range | undefined;
  info: string[];
  date: string;
  actions: string[];
  submissionSteps: SubmissionStep[];
  rawRequiredFields: RawRequiredFields;
  conditionalFields?: ConditionalField[];
  lodgeAI?: LodgeAI;
  isApiLodge?: boolean;
  disclaimer?: string;
}

interface LenderProductContextType {
  product?: LenderProduct;
  lenderCard: UseLenderCardResult;
  lenderCardCustom: UseLenderCardCustomResult;
  quoteCalculatorFormik: FormikProps<QuoteCalculatorFormFields> | undefined;
  showCriteria: boolean;
  showQuoteCalculator: boolean;
  isSingleLenderProduct: boolean;
  showDisclosureStatement: boolean;
  requiredFields?: RequiredFields | undefined;
  isCustomQuote?: boolean;
  inArrears: InArrears;
  setInArrears: (inArrears: InArrears) => void;
  setShowCriteria: (showCriteria: boolean) => void;
  setShowQuoteCalculator: (showQuoteCalculator: boolean) => void;
  setShowDisclosureStatement: (showDisclosureStatement: boolean) => void;
  showRequiredFields: (fields: RemainingDataFields) => void;
}

export type {
  LenderProduct,
  LenderProductContextType,
  CriteriaResult,
  LoadingItem,
  RemainingDataFields,
  ConditionalField,
  Condition,
  Validation,
};
export { ConditionalSection, LogicalOperators, LogicalOperatorRules };
